import {
  ChatMessage,
  ResolvedChatMessage,
  CLIENT_MESSAGE_SENDER,
  AI_MESSAGE_SENDER,
} from '../../types';
import { User } from '../../../user/types';
import React, { ForwardedRef, forwardRef, useCallback, useRef } from 'react';
import { InlineSpinner } from '../../../commons/components/Spinner';
import { TemplateMessage } from './TemplateMessage';
import { Button } from 'react-bootstrap';
import { FooterError } from '../../../commons/components/FooterError';
import { StyleVariables } from '../../../commons/styleConstants';
import { PromptWithPlaceholders } from '../../library/components/PromptWithPlaceholders';
import { ChatType } from '../../dto';
import { ToolCallMessageContent } from '../../../program/components/ai/ToolCallMessageContent';
import { ToolCallResultMessageContent } from '../../../program/components/ai/ToolCallResultMessageContent';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { DivButton } from '../../../commons/components/DivButton';
import { CopyIcon } from '../../../assets/icons';
import { useNotification } from '../../../notification/notificationsContext';
import { SimpleClientTextMessage } from './SimpleClientTextMessage';

export const TextMessage = forwardRef(
  (
    props: {
      chatType: ChatType;
      message: ResolvedChatMessage;
      user: User;
      onSendAgain: (message: ChatMessage) => void;
      maxAiResponses: number;
    },
    ref: ForwardedRef<HTMLDivElement>
  ) => {
    const { chatType, message, user, onSendAgain, maxAiResponses } = props;
    const isTokenLimitError =
      message.errorMessage && message.errorMessage.indexOf('token limit') > 0;

    const messageContentRef = useRef<HTMLDivElement | null>(null);

    const { notifySuccess, notifyError } = useNotification();

    const onCopy = useCallback(async () => {
      try {
        if (!messageContentRef.current) return;

        const tempTextAreaRichText = document.createElement('textarea');
        tempTextAreaRichText.style.display = 'none';
        tempTextAreaRichText.value = messageContentRef.current.innerHTML;
        document.body.appendChild(tempTextAreaRichText);
        tempTextAreaRichText.select();

        const tempTextAreaPlainText = document.createElement('textarea');
        tempTextAreaPlainText.style.display = 'none';
        tempTextAreaPlainText.value = messageContentRef.current.innerText;
        document.body.appendChild(tempTextAreaPlainText);
        tempTextAreaPlainText.select();

        await navigator.clipboard.write([
          new ClipboardItem({
            'text/html': new Blob([tempTextAreaRichText.value], { type: 'text/html' }),
            'text/plain': new Blob([tempTextAreaPlainText.value], { type: 'text/plain' }),
          }),
        ]);
        document.body.removeChild(tempTextAreaRichText);
        document.body.removeChild(tempTextAreaPlainText);
        notifySuccess({ notificationMsg: 'Copied to clipboard' });
      } catch (e) {
        notifyError({ notificationMsg: `Failed to copy to clipboard` });
      }
    }, [notifySuccess, notifyError]);

    return (
      <TemplateMessage
        sender={message.sender === CLIENT_MESSAGE_SENDER ? user : AI_MESSAGE_SENDER}
        chatType={chatType}
        ref={ref}
      >
        <div className="message-list__message-content">
          {message.isPlaceholder ? (
            <div
              className="message-list__message-loading"
              data-test="message-list__message-loading"
            >
              Working on it…
              <InlineSpinner />
            </div>
          ) : (
            <div>
              <div
                style={{
                  color: message.sendFailed ? StyleVariables.neutral500 : undefined,
                }}
                className="fs-mask"
                data-test="message-list__message-content"
              >
                {message.sender === CLIENT_MESSAGE_SENDER && message.wizardStepNumber === 0 ? (
                  <PromptWithPlaceholders
                    libraryPromptContent={message.content ? message.content : ''}
                  />
                ) : message.resolvedToolCalls && message.resolvedToolCalls.length > 0 ? (
                  <ToolCallMessageContent message={message} />
                ) : message.resolvedToolCallResults &&
                  message.resolvedToolCallResults.length > 0 ? (
                  <ToolCallResultMessageContent message={message} />
                ) : message.sender === AI_MESSAGE_SENDER ? (
                  <div ref={messageContentRef}>
                    <Markdown
                      className="message-list__message-ai-sub-content markdown-container"
                      remarkPlugins={[remarkGfm]}
                    >
                      {message.content}
                    </Markdown>
                  </div>
                ) : (
                  <div ref={messageContentRef}>
                    <SimpleClientTextMessage content={message.content} />
                  </div>
                )}
              </div>
              <div className="d-flex justify-content-end align-items-center">
                {message.content && <DivButton icon={<CopyIcon />} onClick={onCopy} />}
                {message.sender === 'ai' && message.aiMessageNumber !== undefined && (
                  <span className="growegy-font12--weight800">{`${message.aiMessageNumber} of ${maxAiResponses}`}</span>
                )}
              </div>
              {message.sendFailed && (
                <div className="d-flex flex-row" style={{ columnGap: '36px', marginTop: '8px' }}>
                  <FooterError
                    message={
                      isTokenLimitError && message.errorMessage
                        ? message.errorMessage
                        : 'This message was not sent.'
                    }
                    dataTest="message-list__error-message"
                  />
                  {!isTokenLimitError && (
                    <Button
                      className="button-apply"
                      onClick={() => onSendAgain(message)}
                      data-test="message-list__send-again-message"
                    >
                      Send again
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </TemplateMessage>
    );
  }
);
